<script lang="ts">
    import { get } from 'svelte/store';
    import { afterUpdate, onMount } from 'svelte';
    import { _ } from 'svelte-i18n';

    import { TacticalPlanRuleApi, type TacticalPlanRuleResponseType } from './TacticalPlanRuleApi';
    import { TacticalPlanApi, type TacticalPlanResponseType } from './TacticalPlanApi';
    import { ApiFilterBuilder } from '@automapa/svelte-automapa-template';
    import TacticalPlanRuleList from './TacticalPlanRuleList.svelte';
    import MessageDanger from '../../shared/Messages/MessageDanger.svelte';
    import type { VehicleResponseType } from '../Vehicles/types';
    import { VehiclesApi } from '../Vehicles/VehiclesApi';
    import Spinner from '../../shared/Spinner/Spinner.svelte';
    import { api } from '../../../stores/api';
    import {
        currentZoneList,
        debug,
        tacticalPlanRuleCurrent,
        tacticalPlanStore,
    } from '../../../stores/store';
    import { getEmptyDefaultRule, TacticalPlanRule } from './TacticalPlanRule';
    import { tacticalPlanName } from '../../../stores/store';
    import { TacticalPlanValidator } from './TacticalPlanValidator';
    import { TacticalPlan } from './TacticalPlan';
    import ErrorInline from '../../shared/ErrorInline.svelte';
    import { ZoneApi, type SimpleZoneType } from '../Zones/ZoneApi';
    import { TacticalPlanContext } from './TacticalPlanContext';
    import { ValidatorReport } from './ValidatorReport';
    import { TacticalPlanAnalizator } from './TacticalPlanAnalizator';
    import ButtonRoundAdd from '../../shared/ButtonRoundAdd.svelte';
    import { waitForZoneSave } from './waitForZoneSave';
    import Opacity from '../../shared/Opacity.svelte';
    import { WarehouseApi } from '../Warehouses/WarehouseApi';
    import { WarehousesElements } from '../Warehouses/WarehousesElements';
    import { MapElements } from '../../utilities/MapElements';
    import { Warehouse } from '../Warehouses/Warehouse';
    import { onMapReady } from '../../utilities/onMapReady';
    import Switch from '../../shared/Switch.svelte';
    import { app } from '../../../stores/app';
    import { Umami } from '../../utilities/Umami';
    import { features, fm } from '../../shared/Features/features';

    export let id: number;

    let tacticalPlan: TacticalPlanResponseType;
    let tacticalPlanRules: TacticalPlanRule[] = [];
    let tacticalPlanRulesOriginal: TacticalPlanRule[] = [];
    let simpleZones: SimpleZoneType[] | null = null;
    let vehicles: VehicleResponseType[] = [];
    let loading: boolean = true;
    let error: string = '';
    let tacticalPlanValidatorReport = new ValidatorReport();
    let tacticalPlanAnalizator = new TacticalPlanAnalizator();
    let context: TacticalPlanContext;
    let alreadyUsedVehicles: number[] = [];
    let warehousesElements: WarehousesElements | null = null;

    function addEmptyRule() {
        waitForZoneSave(async () => {
            $tacticalPlanRuleCurrent = null;
            $tacticalPlanStore.triggerStartSaveThisRuleByInnerId = '';
            tacticalPlanRules = await addEmptyRuleBefore(
                tacticalPlanRules,
                tacticalPlanRules[0],
                false,
                null
            );
        });
    }

    function getUniqueMapIds(tacticalPlanRules: TacticalPlanRule[]): number[] {
        let mapIds = [
            ...new Set(
                tacticalPlanRules
                    .filter(rule => rule.getRule().details.action.type === 'map')
                    .map(rule => rule.getRule().details.action.map_id)
            ),
        ];
        return mapIds;
    }

    function reloadData(id: number) {
        Promise.all([
            TacticalPlanApi.getOne(get(api), id),
            TacticalPlanRuleApi.filter(
                get(api),
                new ApiFilterBuilder(null).addFilterByNumber('tactical_plan_id', id)
            ),
            VehiclesApi.getAll(get(api)),
            WarehouseApi.getAll(get(api)),
            onMapReady()
        ])
            .then(response => {
                tacticalPlan = response[0];
                // console.log('tacticalPlan', tacticalPlan);
                tacticalPlanRules = response[1].map(rule => {
                    return new TacticalPlanRule(rule, false);
                });
                console.log(
                    'tacticalPlanRules, reloadData, before',
                    response[1].map(rule => rule.details)
                );
                console.log(
                    'tacticalPlanRules, reloadData',
                    tacticalPlanRules.map(rule => rule.getRule().details)
                );
                tacticalPlanRulesOriginal = [...tacticalPlanRules];
                vehicles = response[2];
                $tacticalPlanName = tacticalPlan.name;

                if (warehousesElements) {
                    console.log('warehousesElements, add warehouse', response[3]);
                    warehousesElements.setWarehouses(
                        response[3].map(r => new Warehouse().set(r, r.id))
                    );
                    // warehousesElements.zoomToAllWarehouses();
                    warehousesElements.drawWarehouses();
                }

                let mapIdsUsedInThisTacticalPlanRules = getUniqueMapIds(tacticalPlanRules);
                if (mapIdsUsedInThisTacticalPlanRules.length > 0) {
                    ZoneApi.getSimpleZones(get(api), mapIdsUsedInThisTacticalPlanRules)
                        .then(response => {
                            simpleZones = response;
                        })
                        .catch(err => {
                            error = err;
                        });
                } else {
                    simpleZones = null;
                }

                error = '';
            })
            .catch(e => {
                error = e;
            })
            .finally(() => {
                loading = false;
            });
    }

    function saveTacticalPlanWithRules(tacticalPlanRules: TacticalPlanRule[]) {
        console.log('saveTacticalPlanRules', tacticalPlanRules);
        let promisesPlan: Promise<TacticalPlanResponseType | void>[] = [];
        let promisesRules: Promise<TacticalPlanRuleResponseType | void>[] = [];

        loading = true;
        Promise.all(promisesPlan)
            .then(response => {
                if (response[0] && response[0]?.id !== null) {
                    tacticalPlan.id = response[0].id;
                }

                // update or create new rules
                tacticalPlanRules.forEach(rule => {
                    let { details, tactical_plan_id, name, priority, auto_confirm, ...__rest } =
                        rule.getRule();
                    if (tactical_plan_id !== tacticalPlan.id) {
                        tactical_plan_id = tacticalPlan.id;
                    }
                    let id = rule.getId();
                    if (id) {
                        promisesRules.push(
                            TacticalPlanRuleApi.updateOne(get(api), id, {
                                details,
                                tactical_plan_id,
                                name,
                                priority,
                                auto_confirm,
                            })
                        );
                    } else {
                        promisesRules.push(
                            TacticalPlanRuleApi.createOne(get(api), {
                                details,
                                tactical_plan_id,
                                name,
                                priority,
                                auto_confirm,
                            })
                        );
                    }
                });

                // remove deleted rules
                tacticalPlanRulesOriginal.forEach(rule => {
                    let ruleId = rule.getId();
                    if (!tacticalPlanRules.find(r => r.getId() === ruleId) && ruleId) {
                        promisesRules.push(TacticalPlanRuleApi.removeOne(get(api), ruleId));
                    }
                });

                Promise.all(promisesRules)
                    .then(response => {
                        console.log('saveTacticalPlanRules', response);
                        Umami.event('tactical_plan_edit', { id: tacticalPlan.id });
                        tacticalPlanRulesOriginal = [...tacticalPlanRules];
                        error = '';
                        if (tacticalPlan.id) reloadData(tacticalPlan.id);
                    })
                    .catch(e => {
                        Umami.error('tactical_plan_edit', e, { id: tacticalPlan.id });
                        console.warn('error', e);
                        error = $_('save_changes_failed');
                    })
                    .finally(() => {
                        loading = false;
                    });

                (async () => {
                    simpleZones = await ZoneApi.getSimpleZones(
                        get(api),
                        getUniqueMapIds(tacticalPlanRules)
                    );
                })();
            })
            .catch(e => {
                console.warn('error', e);
                error = $_('save_changes_failed');
                loading = false;
            });
    }

    async function addEmptyRuleBefore(
        tacticalPlanRules: TacticalPlanRule[],
        rule: TacticalPlanRule,
        deepcopy: boolean,
        currentPriority: number | null = null
    ): Promise<TacticalPlanRule[]> {
        // let index = tacticalPlanRules.indexOf(rule);
        // if (index>=0) {
        let newRule: TacticalPlanRule;
        currentPriority = currentPriority === null ? rule.getRule().priority : currentPriority;
        tacticalPlanRules.forEach((r, i) => {
            let ruleId = r.getId();
            if (r.getRule().priority > currentPriority && ruleId) {
                r.getRule().priority = r.getRule().priority + 1;
                TacticalPlanRuleApi.patch(get(api), ruleId, '/priority', r.getRule().priority);
            }
        });
        if (deepcopy) {
            let response1 = await TacticalPlanRuleApi.deepcopy(get(api), rule.getId());
            newRule = new TacticalPlanRule(response1, false);
            newRule.getRule().priority = currentPriority + 1;
            newRule.getRule().tactical_plan_id = tacticalPlan.id; // FIXME: add target_tactical_plan_id
            let response2 = await TacticalPlanRuleApi.patch(
                get(api),
                newRule.getId(),
                '/priority',
                newRule.getRule().priority
            );
            let response3 = await TacticalPlanRuleApi.patch(
                get(api),
                newRule.getId(),
                '/tactical_plan_id',
                newRule.getRule().tactical_plan_id
            );
        } else {
            newRule = getEmptyDefaultRule(tacticalPlan.id);
            newRule.getRule().priority = currentPriority + 1;
            let response1 = await TacticalPlanRuleApi.createOne(
                get(api),
                newRule.getRuleReadyToRequest()
            );
        }

        let response9 = await TacticalPlanRuleApi.getByTacticalPlanId(get(api), tacticalPlan.id);
        tacticalPlanRules = response9.map(rule => {
            return new TacticalPlanRule(rule, false);
        });

        return tacticalPlanRules;
    }

    onMount(() => {
        $tacticalPlanStore.countedErrors = 0;
        warehousesElements = new WarehousesElements();
        reloadData(id);

        let savedVolumeData = localStorage.getItem('zoneEditorJobVolume');
        if (savedVolumeData) {
            savedVolumeData = JSON.parse(savedVolumeData);
            if (savedVolumeData && savedVolumeData.expire > (new Date()).getTime()) {
                $tacticalPlanStore.volume = savedVolumeData.value;
            }
        }

        return () => {
            if (warehousesElements) {
                console.log('warehousesElements.clearMap(), tacticalPlanOne');
                warehousesElements.clearMap();
                warehousesElements = null;
            }
        };
    });

    $tacticalPlanStore.countedErrors = 0;

    afterUpdate(() => {
        // console.log('TacticalPlanOne, afterUpdate');
        if (tacticalPlanRules.length > 0) {
            (async () => {
                let reload = simpleZones === null;
                if (simpleZones === null) {
                    simpleZones = await ZoneApi.getSimpleZones(
                        get(api),
                        getUniqueMapIds(tacticalPlanRules)
                    );
                    // console.log('TacticalPlanOne, simpleZones', simpleZones);
                }
                // console.log('TacticalPlanOne, ValidatorReport');
                tacticalPlanValidatorReport = new ValidatorReport();
                context = new TacticalPlanContext(
                    new TacticalPlan(tacticalPlan),
                    tacticalPlanRules,
                    simpleZones,
                    $tacticalPlanRuleCurrent,
                    $currentZoneList
                );
                new TacticalPlanValidator().testAll(tacticalPlanValidatorReport, context);

                if (true) {
                    alreadyUsedVehicles = [
                        ...new Set(tacticalPlanAnalizator.getAlreadyUsedVehicles(context)),
                    ];
                }
                $tacticalPlanStore.countedErrors = tacticalPlanValidatorReport.countErrors();
            })();
        }
    });

    $: alreadyUsedVehicles = [...new Set(alreadyUsedVehicles)];
</script>

{#if loading}
    <Spinner />
{:else}
    <div
        style="display: flex; align-items: center; justify-content: space-between; margin: 20px 0 10px 0; padding:12px 5px 12px 0;"
    >
        <div>
            <div style="font-size: 17px; font-weight: 500;">{$_('When order')}:</div>
            <Opacity>
                <div style="font-size: 15px;">({$_('rules implemented from top to bottom')})</div>
            </Opacity>
        </div>
        <div>
            <ButtonRoundAdd on:click={addEmptyRule} />
        </div>
    </div>
    {#if $debug && tacticalPlanValidatorReport && tacticalPlanValidatorReport.hasMessagesForRuleId(null)}
        {#each tacticalPlanValidatorReport.getMessagesForRuleId(null) as item}
            <ErrorInline text={$_(item.message)} />
        {/each}
    {/if}
    {#if $app.account?.getSettingsReadonly()?.enable_job_modify_time_window_rule}
        <div style="padding: 10px 0; display: flex; flex-direction: row; gap: 10px;">
            <div>
            <Switch checked={true} disabled={true} label={$_('use_time_window_rules')} />
            </div>
            <div>
                <Switch
                    checked={$features?.feature_hide_errors_for_tactical_plan}
                    label={$_('hide_errors_and_warnings')}
                    on:change={e => {
                        $fm.setEnabled('feature_hide_errors_for_tactical_plan', e.detail.checked);
                        $fm = $fm;
                        features.set($fm.getFeatures());
                    }}
                />
            </div>
        </div>
    {/if}
    {#if error}
        <MessageDanger header={$_('Cannot load tactical plans')} {error} />
    {:else if tacticalPlanValidatorReport}
        <div>
            <TacticalPlanRuleList
                bind:tacticalPlanRules
                {vehicles}
                bind:alreadyUsedVehicles
                {addEmptyRuleBefore}
                {saveTacticalPlanWithRules}
                {tacticalPlanValidatorReport}
                bind:simpleZones
            />
        </div>
    {:else}
        <Spinner />
    {/if}
{/if}
